import { atom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

/**
 * Authentication atoms for Agent Portal.
 *
 * Authentication is handled by Keycloak OIDC (ADR-0009).
 * Session information is stored in sessionStorage, populated from the OIDC user profile
 * and enriched from the backend GET /auth/session endpoint.
 * sessionStorage is cleared when the browser tab closes, limiting PII exposure (GDPR data minimisation).
 */

export interface SessionData {
  agentId: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  companyName: string;
  role: string;
  preferredLanguage: string;
  mfaEnabled: boolean;
  expiresAt: string | null;
}

// getOnInit: true prevents redirect flash on refresh
// sessionStorage ensures PII is cleared when the tab closes (GDPR data minimisation)
const sessionDataAtom = atomWithStorage<SessionData | null>(
  "agentPortalSession",
  null,
  createJSONStorage(() => sessionStorage),
  { getOnInit: true },
);
sessionDataAtom.debugLabel = "sessionDataAtom";

export const agentPortalSessionAtom = sessionDataAtom;

export const setSessionAtom = atom(null, (_get, set, session: SessionData | null) => {
  set(sessionDataAtom, session);
});
setSessionAtom.debugLabel = "setSessionAtom";

export const userIdAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.agentId || "";
});
userIdAtom.debugLabel = "userIdAtom";

export const userEmailAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.email || "";
});
userEmailAtom.debugLabel = "userEmailAtom";

export const userFullNameAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.fullName || "";
});
userFullNameAtom.debugLabel = "userFullNameAtom";

export const userFirstNameAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.firstName || "";
});
userFirstNameAtom.debugLabel = "userFirstNameAtom";

export const userLastNameAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.lastName || "";
});
userLastNameAtom.debugLabel = "userLastNameAtom";

export const userRoleAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.role || "";
});
userRoleAtom.debugLabel = "userRoleAtom";

export const companyNameAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.companyName || "";
});
companyNameAtom.debugLabel = "companyNameAtom";

export const agencyNameAtom = atomWithStorage<string>("agentPortalAgencyName", "");
agencyNameAtom.debugLabel = "agencyNameAtom";

export const preferredLanguageAtom = atom<string>((get) => {
  const session = get(sessionDataAtom);
  return session?.preferredLanguage || "en";
});
preferredLanguageAtom.debugLabel = "preferredLanguageAtom";

export const mfaEnabledAtom = atom<boolean>((get) => {
  const session = get(sessionDataAtom);
  return session?.mfaEnabled || false;
});
mfaEnabledAtom.debugLabel = "mfaEnabledAtom";

export const sessionExpiresAtAtom = atom<Date | null>((get) => {
  const session = get(sessionDataAtom);
  if (!session?.expiresAt) return null;
  return new Date(session.expiresAt);
});
sessionExpiresAtAtom.debugLabel = "sessionExpiresAtAtom";

export const isAuthenticatedAtom = atom((get) => {
  const session = get(sessionDataAtom);
  const expiresAt = get(sessionExpiresAtAtom);

  if (!session || !expiresAt) {
    return false;
  }

  return new Date() < expiresAt;
});
isAuthenticatedAtom.debugLabel = "isAuthenticatedAtom";

export const authLoadingAtom = atom<boolean>(true);
authLoadingAtom.debugLabel = "authLoadingAtom";

export const authErrorAtom = atom<string | null>(null);
authErrorAtom.debugLabel = "authErrorAtom";
